package features.booking

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.gitlive.firebase.auth.auth
import features.BaseViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.first
import kotlin.time.Clock
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.contentType
import io.ktor.http.ContentType
import io.ktor.client.call.body
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull

class AdminViewModel(
    private val catalogRepository: CatalogRepository,
    private val userRepository: UserRepository,
    private val bookingRepository: BookingRepository
) : BaseViewModel() {
    val fechaDesde = MutableStateFlow(org.example.abigail.obtenerFechaHoy().dropLast(2) + "01")
    val fechaHasta = MutableStateFlow(org.example.abigail.obtenerFechaHoy())

    private val _bookings = MutableStateFlow<List<Boleto>>(emptyList())
    val bookings: StateFlow<List<Boleto>> = _bookings.asStateFlow()

    val rutaSeleccionada = MutableStateFlow("")
    val fechaSeleccionada = MutableStateFlow(org.example.abigail.obtenerFechaHoy())

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _ciudades = MutableStateFlow<List<String>>(emptyList())
    val ciudades: StateFlow<List<String>> = _ciudades.asStateFlow()

    private val _horarios = MutableStateFlow<List<String>>(emptyList())
    val horarios: StateFlow<List<String>> = _horarios.asStateFlow()

    private val _paradas = MutableStateFlow<List<String>>(emptyList())
    val paradas: StateFlow<List<String>> = _paradas.asStateFlow()

    private val _paradasInfo = MutableStateFlow<List<ParadaInfo>>(emptyList())
    val paradasInfo: StateFlow<List<ParadaInfo>> = _paradasInfo.asStateFlow()

    private val _puntosSubida = MutableStateFlow<List<String>>(emptyList())
    val puntosSubida: StateFlow<List<String>> = _puntosSubida.asStateFlow()

    private val _puntosSubidaInfo = MutableStateFlow<List<ParadaInfo>>(emptyList())
    val puntosSubidaInfo: StateFlow<List<ParadaInfo>> = _puntosSubidaInfo.asStateFlow()

    private val _vehiculos = MutableStateFlow<List<String>>(emptyList())
    val vehiculos: StateFlow<List<String>> = _vehiculos.asStateFlow()

    private val _vehiculosConfig = MutableStateFlow<List<VehiculoConfig>>(emptyList())
    val vehiculosConfig: StateFlow<List<VehiculoConfig>> = _vehiculosConfig.asStateFlow()

    private val _layoutViaje = MutableStateFlow<VehiculoConfig?>(null)
    val layoutViaje: StateFlow<VehiculoConfig?> = _layoutViaje.asStateFlow()

    private val _choferes = MutableStateFlow<List<String>>(emptyList())
    val choferes: StateFlow<List<String>> = _choferes.asStateFlow()

    private val _ingresosSemana = MutableStateFlow(0)
    val ingresosSemana: StateFlow<Int> = _ingresosSemana.asStateFlow()

    private val _ingresosMes = MutableStateFlow(0)
    val ingresosMes: StateFlow<Int> = _ingresosMes.asStateFlow()

    // Ingreso de efectivo aún NO cobrado (reservas efectivo con pagoConfirmado=false).
    // Se reporta aparte para no inflar el ingreso confirmado.
    private val _ingresosPendientes = MutableStateFlow(0)
    val ingresosPendientes: StateFlow<Int> = _ingresosPendientes.asStateFlow()

    private val _boletosVendidos = MutableStateFlow(0)
    val boletosVendidos: StateFlow<Int> = _boletosVendidos.asStateFlow()

    private val _rutaMasPopular = MutableStateFlow("Calculando...")
    val rutaMasPopular: StateFlow<String> = _rutaMasPopular.asStateFlow()

    // ═══ Configuración de Pagos (Stripe on/off) ═══
    private val _stripeActivo = MutableStateFlow(true)
    val stripeActivo: StateFlow<Boolean> = _stripeActivo.asStateFlow()

    // #10: GPS de choferes on/off (control de costos de Firebase). Default true.
    private val _gpsChoferesActivo = MutableStateFlow(true)
    val gpsChoferesActivo: StateFlow<Boolean> = _gpsChoferesActivo.asStateFlow()

    fun cargarGpsChoferes() {
        viewModelScope.launch {
            try {
                val snap = network.FirebaseClient.database.reference("config/gpsChoferesActivo").valueEvents.first()
                _gpsChoferesActivo.value = if (snap.exists) snap.value<Boolean>() else true
            } catch (_: Exception) { _gpsChoferesActivo.value = true }
        }
    }

    fun setGpsChoferesActivo(activo: Boolean) {
        if (bloqueadoPorVersion()) return
        _gpsChoferesActivo.value = activo
        viewModelScope.launch {
            try {
                network.FirebaseClient.database.reference("config/gpsChoferesActivo").setValue(activo)
                _mensajeGlobal.value = if (activo) "GPS de choferes ACTIVADO." else "GPS de choferes DESACTIVADO (ahorro de costos)."
            } catch (_: Exception) { _mensajeGlobal.value = "No se pudo cambiar el GPS." }
        }
    }

    /**
     * Relee del servidor para confirmar que la configuración quedó aplicada.
     *
     * Guardar y confiar no basta: si la escritura falla o llega tarde, la pantalla seguía
     * mostrando el valor nuevo aunque el servidor tuviera el viejo. Esto lo detecta y deja
     * los flujos (pago, GPS) alineados con lo que realmente hay guardado.
     */
    suspend fun verificarConfigAplicada(stripeEsperado: Boolean, gpsEsperado: Boolean): Boolean {
        return try {
            val db = network.FirebaseClient.database
            val stripeReal = try {
                val s = db.reference("config/stripeActivo").valueEvents.first()
                if (s.exists) s.value<Boolean>() else true
            } catch (_: Exception) { return false }
            val gpsReal = try {
                val g = db.reference("config/gpsChoferesActivo").valueEvents.first()
                if (g.exists) g.value<Boolean>() else true
            } catch (_: Exception) { return false }
            // Sincronizar el estado local con lo que de verdad hay en el servidor.
            _gpsChoferesActivo.value = gpsReal
            _stripeActivo.value = stripeReal
            network.ConfigPagos.actualizarStripeActivo(stripeReal)
            stripeReal == stripeEsperado && gpsReal == gpsEsperado
        } catch (_: Exception) { false }
    }

    private val _clabeManual = MutableStateFlow("")
    val clabeManual: StateFlow<String> = _clabeManual.asStateFlow()

    private val _beneficiarioManual = MutableStateFlow("")
    val beneficiarioManual: StateFlow<String> = _beneficiarioManual.asStateFlow()

    private val _bancoManual = MutableStateFlow("")
    val bancoManual: StateFlow<String> = _bancoManual.asStateFlow()

    private val _telefonoAtencion = MutableStateFlow("")
    val telefonoAtencion: StateFlow<String> = _telefonoAtencion.asStateFlow()

    private val _pagosManuales = MutableStateFlow<List<PagoManual>>(emptyList())
    val pagosManuales: StateFlow<List<PagoManual>> = _pagosManuales.asStateFlow()

    private val _cargandoPagosManuales = MutableStateFlow(false)
    val cargandoPagosManuales: StateFlow<Boolean> = _cargandoPagosManuales.asStateFlow()

    fun cargarConfigPagos() {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("config").valueEvents.first()
                if (snap.exists) {
                    _stripeActivo.value = try { snap.child("stripeActivo").value<Boolean>() } catch (_: Exception) { true }
                    _clabeManual.value = try { snap.child("clabeManual").value<String>() } catch (_: Exception) { "" }
                    _beneficiarioManual.value = try { snap.child("beneficiarioManual").value<String>() } catch (_: Exception) { "" }
                    _bancoManual.value = try { snap.child("bancoManual").value<String>() } catch (_: Exception) { "" }
                    _telefonoAtencion.value = try { snap.child("telefonoAtencion").value<String>() } catch (_: Exception) { "" }
                }
            } catch (_: Exception) {}
        }
    }

    fun guardarConfigPagos(stripeActivo: Boolean, clabe: String, beneficiario: String, banco: String, telefono: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            try {
                val db = network.FirebaseClient.database
                val updates = mapOf<String, Any>(
                    "stripeActivo" to stripeActivo,
                    "clabeManual" to clabe,
                    "beneficiarioManual" to beneficiario,
                    "bancoManual" to banco,
                    "telefonoAtencion" to telefono
                )
                db.reference("config").updateChildren(updates)
                _stripeActivo.value = stripeActivo
                _clabeManual.value = clabe
                _beneficiarioManual.value = beneficiario
                _bancoManual.value = banco
                _telefonoAtencion.value = telefono
                _mensajeGlobal.value = "Configuración de pagos guardada"
            } catch (e: Exception) {
                _mensajeGlobal.value = "Error al guardar: ${e.message}"
            }
        }
    }

    fun cargarPagosManuales() {
        viewModelScope.launch {
            _cargandoPagosManuales.value = true
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("pagos_manuales").valueEvents.first()
                if (snap.exists) {
                    val lista = mutableListOf<PagoManual>()
                    snap.children.forEach { child ->
                        try {
                            val id = child.key ?: return@forEach
                            lista.add(PagoManual(
                                id = id,
                                ruta = try { child.child("ruta").value<String>() } catch (_: Exception) { "" },
                                fecha = try { child.child("fecha").value<String>() } catch (_: Exception) { "" },
                                horario = try { child.child("horario").value<String>() } catch (_: Exception) { "" },
                                asientos = try { child.child("asientos").value<String>() } catch (_: Exception) { "" },
                                email = try { child.child("email").value<String>() } catch (_: Exception) { "" },
                                totalPagado = try { child.child("totalPagado").value<Int>() } catch (_: Exception) { 0 },
                                pasajeros = try { child.child("pasajeros").value<String>() } catch (_: Exception) { "" },
                                timestamp = try { child.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (_: Exception) { try { child.child("timestamp").value<Long>() } catch (_: Exception) { 0L } },
                                estatus = try { child.child("estatus").value<String>() } catch (_: Exception) { "pendiente" },
                                puntoSubida = try { child.child("puntoSubida").value<String>() } catch (_: Exception) { "" },
                                resueltoPorNombre = try { child.child("resueltoPorNombre").value<String>() } catch (_: Exception) { "" }
                            ))
                        } catch (_: Exception) {}
                    }
                    // Pendientes primero (a resolver); luego los últimos 20 resueltos para ver su estado
                    val pendientes = lista.filter { it.estatus == "pendiente" }.sortedByDescending { it.timestamp }
                    val resueltos = lista.filter { it.estatus != "pendiente" }.sortedByDescending { it.timestamp }.take(20)
                    _pagosManuales.value = pendientes + resueltos
                }
            } catch (_: Exception) {}
            _cargandoPagosManuales.value = false
        }
    }

    fun confirmarPagoManual(pago: PagoManual, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                // La Cloud Function ocupa asientos, crea reserva + QR, limpia bloqueos
                // y envía boletos de forma atómica (evita doble venta y QR sin verificación).
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("pagoId", kotlinx.serialization.json.JsonPrimitive(pago.id))
                    })
                }
                val response = client.post(network.CloudFunctions.CONFIRMAR_PAGO_MANUAL) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val responseText = response.body<String>()
                if (responseText.contains("\"success\":true") || responseText.contains("\"success\": true")) {
                    _mensajeGlobal.value = "Pago confirmado. Boletos enviados a ${pago.email}"
                    cargarPagosManuales()
                    onResult(true)
                } else {
                    val msg = if (responseText.contains("ya ocupados")) {
                        "No se pudo confirmar: hay asientos ya ocupados. Revisa conflictos."
                    } else if (responseText.contains("ya fue procesado")) {
                        "Este pago ya fue procesado."
                    } else {
                        "Error al confirmar el pago. Intenta de nuevo."
                    }
                    _mensajeGlobal.value = msg
                    cargarPagosManuales()
                    onResult(false)
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.confirmarPagoManual[${pago.id}]")
                _mensajeGlobal.value = "Error al confirmar: ${e.message}"
                onResult(false)
            }
        }
    }

    fun declinarPagoManual(pago: PagoManual, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("pagoId", kotlinx.serialization.json.JsonPrimitive(pago.id))
                    })
                }
                val response = client.post(network.CloudFunctions.DECLINAR_PAGO_MANUAL) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val responseText = response.body<String>()
                if (responseText.contains("\"success\":true") || responseText.contains("\"success\": true")) {
                    _mensajeGlobal.value = "Pago declinado. Asientos liberados."
                    cargarPagosManuales()
                    onResult(true)
                } else {
                    _mensajeGlobal.value = if (responseText.contains("ya fue procesado")) "Este pago ya fue procesado." else "Error al declinar el pago."
                    cargarPagosManuales()
                    onResult(false)
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.declinarPagoManual[${pago.id}]")
                _mensajeGlobal.value = "Error al declinar: ${e.message}"
                onResult(false)
            }
        }
    }

    private val _alertas = MutableStateFlow<List<AlertaActividad>>(emptyList())
    val alertas: StateFlow<List<AlertaActividad>> = _alertas.asStateFlow()

    private val _itinerarios = MutableStateFlow<List<ViajeActivo>>(emptyList())
    val itinerarios: StateFlow<List<ViajeActivo>> = _itinerarios.asStateFlow()

    private val _usuariosRegistrados = MutableStateFlow<List<UsuarioApp>>(emptyList())
    val usuariosRegistrados: StateFlow<List<UsuarioApp>> = _usuariosRegistrados.asStateFlow()

    private val _reservasActivas = MutableStateFlow<List<ReservaAdmin>>(emptyList())
    val reservasActivas: StateFlow<List<ReservaAdmin>> = _reservasActivas.asStateFlow()

    private val _reservasPasadas = MutableStateFlow<List<ReservaAdmin>>(emptyList())
    val reservasPasadas: StateFlow<List<ReservaAdmin>> = _reservasPasadas.asStateFlow()

    private val _mapaAsientos = MutableStateFlow<List<AdminSeatInfo>>(emptyList())
    val mapaAsientos: StateFlow<List<AdminSeatInfo>> = _mapaAsientos.asStateFlow()

    private val _cargandoMapa = MutableStateFlow(false)
    val cargandoMapa: StateFlow<Boolean> = _cargandoMapa.asStateFlow()

    fun cargarMapaAsientos(ruta: String, fecha: String, horario: String) {
        viewModelScope.launch {
            _cargandoMapa.value = true
            _mapaAsientos.value = bookingRepository.obtenerMapaAsientosAdmin(ruta, fecha, horario)
            _cargandoMapa.value = false
        }
    }

    // Historial de pagos
    private val _historialPagos = MutableStateFlow<List<PagoRegistro>>(emptyList())
    val historialPagos: StateFlow<List<PagoRegistro>> = _historialPagos.asStateFlow()

    private val _cargandoPagos = MutableStateFlow(false)
    val cargandoPagos: StateFlow<Boolean> = _cargandoPagos.asStateFlow()

    // Cache-timestamp para evitar re-leer historial cada vez que abre la pantalla.
    private var _lastLoadHistorialPagos: Long = 0L
    private val SEIS_HORAS_MS = 6L * 60L * 60L * 1000L

    /**
     * Carga histórico de pagos con cache de 6h.
     * @param force true = ignora cache y consulta al servidor (útil desde botón "Actualizar").
     */
    fun cargarHistorialPagos(force: Boolean = false) {
        viewModelScope.launch {
            val ahora = org.example.abigail.obtenerMilisegundosActuales()
            val cacheValido = _historialPagos.value.isNotEmpty() &&
                _lastLoadHistorialPagos > 0L &&
                (ahora - _lastLoadHistorialPagos) < SEIS_HORAS_MS
            if (!force && cacheValido) return@launch  // Muestra cache; no consulta a Firebase.
            _cargandoPagos.value = true
            _historialPagos.value = bookingRepository.obtenerHistorialPagos()
            _lastLoadHistorialPagos = ahora
            _cargandoPagos.value = false
        }
    }

    // ═══ Bitácora de auditoría (trazabilidad de operador) ═══
    private val _auditoria = MutableStateFlow<List<MovimientoAuditoria>>(emptyList())
    val auditoria: StateFlow<List<MovimientoAuditoria>> = _auditoria.asStateFlow()

    private val _cargandoAuditoria = MutableStateFlow(false)
    val cargandoAuditoria: StateFlow<Boolean> = _cargandoAuditoria.asStateFlow()

    fun cargarAuditoria() {
        viewModelScope.launch {
            _cargandoAuditoria.value = true
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("auditoria").valueEvents.first()
                val lista = mutableListOf<MovimientoAuditoria>()
                if (snap.exists) {
                    snap.children.forEach { c ->
                        try {
                            lista.add(MovimientoAuditoria(
                                id = c.key ?: "",
                                accion = try { c.child("accion").value<String>() } catch (_: Exception) { "" },
                                operadorUid = try { c.child("operadorUid").value<String>() } catch (_: Exception) { "" },
                                operadorNombre = try { c.child("operadorNombre").value<String>() } catch (_: Exception) { "" },
                                ruta = try { c.child("ruta").value<String>() } catch (_: Exception) { "" },
                                fecha = try { c.child("fecha").value<String>() } catch (_: Exception) { "" },
                                horario = try { c.child("horario").value<String>() } catch (_: Exception) { "" },
                                reservaId = try { c.child("reservaId").value<String>() } catch (_: Exception) { "" },
                                pagoId = try { c.child("pagoId").value<String>() } catch (_: Exception) { "" },
                                asientos = try { c.child("asientos").value<String>() } catch (_: Exception) { "" },
                                monto = try { c.child("monto").value<Int>() } catch (_: Exception) { 0 },
                                targetUid = try { c.child("targetUid").value<String>() } catch (_: Exception) { "" },
                                detalle = try { c.child("detalle").value<String>() } catch (_: Exception) { "" },
                                pasajeros = try { c.child("pasajeros").value<String>() } catch (_: Exception) { "" },
                                email = try { c.child("email").value<String>() } catch (_: Exception) { "" },
                                metodoPago = try { c.child("metodoPago").value<String>() } catch (_: Exception) { "" },
                                timestamp = try { c.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (_: Exception) { 0L }
                            ))
                        } catch (_: Exception) {}
                    }
                }
                _auditoria.value = lista.sortedByDescending { it.timestamp }.take(300)
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.cargarAuditoria")
            }
            _cargandoAuditoria.value = false
        }
    }

    // ═══ Cancelaciones y reembolsos ═══
    private val _cancelaciones = MutableStateFlow<List<CancelacionRegistro>>(emptyList())
    val cancelaciones: StateFlow<List<CancelacionRegistro>> = _cancelaciones.asStateFlow()

    private val _cargandoCancelaciones = MutableStateFlow(false)
    val cargandoCancelaciones: StateFlow<Boolean> = _cargandoCancelaciones.asStateFlow()

    // ── Badges del drawer: contadores de pendientes que requieren atención del admin ──
    private val _badgeCancelaciones = MutableStateFlow(0)
    val badgeCancelaciones: StateFlow<Int> = _badgeCancelaciones.asStateFlow()
    private val _badgePagosManuales = MutableStateFlow(0)
    val badgePagosManuales: StateFlow<Int> = _badgePagosManuales.asStateFlow()
    private val _badgeConflictos = MutableStateFlow(0)
    val badgeConflictos: StateFlow<Int> = _badgeConflictos.asStateFlow()
    private val _badgeFacturas = MutableStateFlow(0)
    val badgeFacturas: StateFlow<Int> = _badgeFacturas.asStateFlow()

    /** Lee (ligero) los contadores de pendientes para pintar las burbujitas del drawer.
     *  Cada consulta es acotada e indexada; se llama al abrir el panel y al abrir el drawer. */
    fun cargarBadgesPendientes() {
        viewModelScope.launch {
            val db = network.FirebaseClient.database
            // Cancelaciones pendientes (estadoResolucion == "pendiente")
            try {
                val s = db.reference("historial_cancelaciones").orderByChild("estadoResolucion").equalTo("pendiente").valueEvents.first()
                _badgeCancelaciones.value = if (s.exists) s.children.count() else 0
            } catch (_: Exception) {}
            // Pagos manuales pendientes de confirmar
            try {
                val s = db.reference("pagos_manuales").orderByChild("estatus").equalTo("pendiente").valueEvents.first()
                _badgePagosManuales.value = if (s.exists) s.children.count() else 0
            } catch (_: Exception) {}
            // Conflictos de asientos sin resolver
            try {
                val s = db.reference("conflictos_asientos").valueEvents.first()
                _badgeConflictos.value = if (s.exists) s.children.count { c ->
                    val st = try { c.child("status").value<String>() } catch (_: Exception) { "" }
                    st != "resuelto" && st != "reembolsado"
                } else 0
            } catch (_: Exception) {}
            // Solicitudes de factura pendientes
            try {
                val s = db.reference("solicitudes_factura").valueEvents.first()
                _badgeFacturas.value = if (s.exists) s.children.count { c ->
                    val st = try { c.child("estatus").value<String>() } catch (_: Exception) { "pendiente" }
                    st == "pendiente"
                } else 0
            } catch (_: Exception) {}
        }
    }

    fun cargarCancelaciones() {
        viewModelScope.launch {
            _cargandoCancelaciones.value = true
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("historial_cancelaciones").valueEvents.first()
                val lista = mutableListOf<CancelacionRegistro>()
                if (snap.exists) {
                    snap.children.forEach { c ->
                        try {
                            lista.add(CancelacionRegistro(
                                id = c.key ?: "",
                                ruta = try { c.child("ruta").value<String>() } catch (_: Exception) { "" },
                                fecha = try { c.child("fecha").value<String>() } catch (_: Exception) { "" },
                                horario = try { c.child("horario").value<String>() } catch (_: Exception) { "" },
                                reservaId = try { c.child("reservaId").value<String>() } catch (_: Exception) { "" },
                                pasajeros = try { c.child("pasajeros").value<String>() } catch (_: Exception) { "" },
                                correo = try { c.child("correo").value<String>() } catch (_: Exception) { "" },
                                telefono = run {
                                    var tel = ""
                                    c.child("snapPasajeros").children.forEach { p ->
                                        if (tel.isBlank()) tel = try { p.child("telefono").value<String>() } catch (_: Exception) { "" }
                                    }
                                    tel
                                },
                                totalReembolsar = try { c.child("totalReembolsar").value<Int>() } catch (_: Exception) { 0 },
                                canceladoPorNombre = try { c.child("canceladoPorNombre").value<String>() } catch (_: Exception) { "" },
                                origen = try { c.child("origen").value<String>() } catch (_: Exception) { "" },
                                estadoResolucion = try { c.child("estadoResolucion").value<String>().ifBlank { "pendiente" } } catch (_: Exception) { "pendiente" },
                                resueltoPorNombre = try { c.child("resueltoPorNombre").value<String>() } catch (_: Exception) { "" },
                                reasignadoA = try { c.child("reasignadoA").value<String>() } catch (_: Exception) { "" },
                                fechaCancelacion = try { c.child("fechaCancelacion").value<String>().toLongOrNull() ?: 0L } catch (_: Exception) { 0L },
                                fechaResolucion = try { c.child("fechaResolucion").value<String>().toLongOrNull() ?: 0L } catch (_: Exception) { 0L },
                                asientosOriginales = run {
                                    val seats = mutableListOf<Int>()
                                    c.child("snapPasajeros").children.forEach { p ->
                                        try { seats.add(p.child("asiento").value<Int>()) } catch (_: Exception) {}
                                    }
                                    seats
                                }
                            ))
                        } catch (_: Exception) {}
                    }
                }
                _cancelaciones.value = lista.sortedByDescending { it.fechaCancelacion }.take(300)
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.cargarCancelaciones")
            }
            _cargandoCancelaciones.value = false
        }
    }

    // Viajes disponibles como destino de reasignación
    private val _viajesDisponibles = MutableStateFlow<List<ViajeDisponibleReasignacion>>(emptyList())
    val viajesDisponibles: StateFlow<List<ViajeDisponibleReasignacion>> = _viajesDisponibles.asStateFlow()

    private val _cargandoViajesDisponibles = MutableStateFlow(false)
    val cargandoViajesDisponibles: StateFlow<Boolean> = _cargandoViajesDisponibles.asStateFlow()

    fun cargarViajesDisponibles() {
        viewModelScope.launch {
            _cargandoViajesDisponibles.value = true
            _viajesDisponibles.value = bookingRepository.obtenerViajesProgramadosDisponibles()
            _cargandoViajesDisponibles.value = false
        }
    }

    /** Devuelve (capacidad, asientosLibres) del viaje destino — para el selector de asientos al reasignar. */
    suspend fun asientosLibresDeViaje(ruta: String, fecha: String, horario: String): Pair<Int, List<Int>> {
        return bookingRepository.obtenerAsientosLibresViaje(ruta, fecha, horario)
    }

    /** Reasigna una cancelación a un viaje destino: recrea boleto + QR y reenvía correo.
     *  asientosElegidos=null → mantiene el asiento original si está libre, si no, rellena. */
    fun reasignarCancelacion(cancelacionId: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, asientosElegidos: List<Int>? = null, onResult: (ReasignacionResultado) -> Unit) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) { onResult(ReasignacionResultado(false, "Actualiza la app para reasignar.")); return@launch }
            val res = bookingRepository.reasignarReservaCancelada(cancelacionId, rutaNueva, fechaNueva, horarioNuevo, asientosElegidos)
            if (res.ok) {
                // Recargar desde DB para traer reasignadoA + resueltoPorNombre ya persistidos.
                cargarCancelaciones()
            }
            _mensajeGlobal.value = res.mensaje
            onResult(res)
        }
    }

    /** Marca una cancelación como reembolsada o reasignada (seguimiento manual). */
    fun marcarResolucionCancelacion(id: String, estado: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            try {
                val db = network.FirebaseClient.database
                val uid = network.FirebaseClient.auth.currentUser?.uid ?: ""
                val nombre = try {
                    val u = db.reference("usuarios/$uid").valueEvents.first()
                    val n = try { u.child("nombre").value<String?>() } catch (_: Exception) { null }
                    val co = try { u.child("correo").value<String?>() } catch (_: Exception) { null }
                    n?.takeIf { it.isNotBlank() } ?: co?.takeIf { it.isNotBlank() }
                        ?: network.FirebaseClient.auth.currentUser?.email ?: ""
                } catch (_: Exception) { network.FirebaseClient.auth.currentUser?.email ?: "" }
                val updates = mapOf<String, Any?>(
                    "historial_cancelaciones/$id/estadoResolucion" to estado,
                    "historial_cancelaciones/$id/resueltoPorUid" to uid,
                    "historial_cancelaciones/$id/resueltoPorNombre" to nombre,
                    "historial_cancelaciones/$id/fechaResolucion" to org.example.abigail.obtenerMilisegundosActuales().toString()
                )
                db.reference().updateChildren(updates)
                // Reflejar de inmediato en UI
                _cancelaciones.value = _cancelaciones.value.map {
                    if (it.id == id) it.copy(estadoResolucion = estado, resueltoPorNombre = nombre) else it
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.marcarResolucionCancelacion")
            }
        }
    }

    // Conflictos de asientos
    private val _conflictos = MutableStateFlow<List<ConflictoAsiento>>(emptyList())
    val conflictos: StateFlow<List<ConflictoAsiento>> = _conflictos.asStateFlow()

    private val _cargandoConflictos = MutableStateFlow(false)
    val cargandoConflictos: StateFlow<Boolean> = _cargandoConflictos.asStateFlow()

    fun cargarConflictos() {
        viewModelScope.launch {
            _cargandoConflictos.value = true
            _conflictos.value = bookingRepository.obtenerConflictosAsientos()
            _cargandoConflictos.value = false
        }
    }

    fun reasignarAsiento(conflictoId: String, asientoViejo: Int, asientoNuevo: Int, pasajero: PasajeroInfo, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val exito = bookingRepository.reasignarAsientoConflicto(conflictoId, asientoViejo, asientoNuevo, pasajero)
            if (exito) cargarConflictos()
            onResult(exito)
        }
    }

    fun marcarReembolsado(conflictoId: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val exito = bookingRepository.marcarConflictoReembolsado(conflictoId)
            if (exito) cargarConflictos()
            onResult(exito)
        }
    }

    val idSesionActual = "ADMIN_${org.example.abigail.obtenerIdSesionPersistente()}"
    private val _tiempoRestanteMilis = MutableStateFlow(0L)
    val tiempoRestanteMilis: StateFlow<Long> = _tiempoRestanteMilis.asStateFlow()
    private val _mostrarTemporizador = MutableStateFlow(false)
    val mostrarTemporizador: StateFlow<Boolean> = _mostrarTemporizador.asStateFlow()
    private var timerJob: kotlinx.coroutines.Job? = null

    private val _mensajeReasignacion = MutableStateFlow<String?>(null)
    val mensajeReasignacion: StateFlow<String?> = _mensajeReasignacion.asStateFlow()

    private val _mensajeGlobal = MutableStateFlow<String?>(null)
    val mensajeGlobal: StateFlow<String?> = _mensajeGlobal.asStateFlow()

    fun limpiarMensajeGlobal() { _mensajeGlobal.value = null }

    // ═══ Búsqueda de boletos por pasajero ═══
    private val _resultadosBusqueda = MutableStateFlow<List<ResultadoBusqueda>>(emptyList())
    val resultadosBusqueda: StateFlow<List<ResultadoBusqueda>> = _resultadosBusqueda.asStateFlow()

    private val _buscandoBoletos = MutableStateFlow(false)
    val buscandoBoletos: StateFlow<Boolean> = _buscandoBoletos.asStateFlow()

    fun buscarBoletos(termino: String) {
        if (termino.isBlank() || termino.length < 2) {
            _resultadosBusqueda.value = emptyList()
            return
        }
        val query = termino.trim().lowercase()
        viewModelScope.launch {
            _buscandoBoletos.value = true
            val todas = (_reservasActivas.value + _reservasPasadas.value)
            val resultados = mutableListOf<ResultadoBusqueda>()
            for (reserva in todas) {
                for (pasajero in reserva.pasajeros) {
                    val match = pasajero.nombre.lowercase().contains(query) ||
                            pasajero.telefono.contains(query) ||
                            pasajero.correo.lowercase().contains(query)
                    if (match) {
                        // Usar puntoSubida de la reserva; si no tiene, buscar del viaje programado como fallback
                        resultados.add(ResultadoBusqueda(reserva = reserva, pasajero = pasajero, puntoSubida = reserva.puntoSubida))
                    }
                }
            }
            // Enriquecer con punto de subida del viaje programado cuando la reserva no tiene uno propio
            val puntosCache = mutableMapOf<String, String>()
            val enriquecidos = resultados.map { r ->
                if (r.puntoSubida.isNotBlank()) return@map r
                val cacheKey = "${r.reserva.ruta}|${r.reserva.fecha}|${r.reserva.horario}"
                if (!puntosCache.containsKey(cacheKey)) {
                    val punto = try {
                        val db = network.FirebaseClient.database
                        val snap = db.reference("viajes_programados/${r.reserva.ruta}/${r.reserva.fecha}/${r.reserva.horario}/puntoSubida").valueEvents.first()
                        if (snap.exists) snap.value<String>() else ""
                    } catch (_: Exception) { "" }
                    puntosCache[cacheKey] = punto
                }
                r.copy(puntoSubida = puntosCache[cacheKey] ?: "")
            }
            _resultadosBusqueda.value = enriquecidos.sortedByDescending { it.reserva.fecha }
            _buscandoBoletos.value = false
        }
    }

    fun limpiarBusqueda() {
        _resultadosBusqueda.value = emptyList()
    }

    /**
     * Obtiene los puntos de subida disponibles para una ruta específica en una fecha/horario.
     * Primero busca el punto asignado al viaje programado, luego todos los del catálogo.
     */
    suspend fun obtenerPuntosSubidaDisponibles(ruta: String, fecha: String, horario: String): List<String> {
        return try {
            // Retornar todos los puntos del catálogo de puntosSubida
            _puntosSubida.value.toList()
        } catch (_: Exception) { emptyList() }
    }

    /**
     * Cambia el punto de subida de una reserva en Firebase y actualiza la lista local.
     */
    /** Asigna/cambia la parada de BAJADA de un pasajero (por asiento) dentro de una reserva. */
    fun cambiarParadaPasajero(reserva: ReservaAdmin, asiento: Int, nuevaParada: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val basePas = "viajes/${reserva.ruta}/${reserva.fecha}/${reserva.horario}/reservas/${reserva.idReserva}/pasajeros"
                val snap = db.reference(basePas).valueEvents.first()
                var idxKey: String? = null
                if (snap.exists) {
                    snap.children.forEach { c ->
                        val a = try { c.child("asiento").value<Int>() } catch (e: Exception) { -1 }
                        if (a == asiento && idxKey == null) idxKey = c.key
                    }
                }
                if (idxKey == null) { onResult(false); return@launch }
                db.reference("$basePas/$idxKey/parada").setValue(nuevaParada)
                // Refrescar mapa de asientos + reservas para reflejar el cambio
                cargarMapaAsientos(reserva.ruta, reserva.fecha, reserva.horario)
                cargarGestionDeBoletos()
                onResult(true)
            } catch (e: Exception) {
                onResult(false)
            }
        }
    }

    /**
     * Código QR REAL de un asiento (el que escanea el chofer), leído de verificaciones.
     * El boleto descargado desde Gestión de Boletos reconstruía "SMARTVAN-{idReserva}-{asiento}",
     * que NO coincide con la key guardada al comprar → el chofer lo leía como "no pertenece".
     */
    suspend fun codigoQrDeAsiento(reserva: ReservaAdmin, asiento: Int): String {
        return try {
            val db = network.FirebaseClient.database
            val snap = db.reference("viajes/${reserva.ruta}/${reserva.fecha}/${reserva.horario}/verificaciones")
                .valueEvents.first()
            if (!snap.exists) return ""
            var encontrado = ""
            snap.children.forEach { v ->
                val rid = try { v.child("reservaId").value<String>() } catch (_: Exception) { "" }
                val asi = try { v.child("asiento").value<Int>() } catch (_: Exception) {
                    try { v.child("asiento").value<String>().toIntOrNull() ?: -1 } catch (_: Exception) { -1 }
                }
                if (rid == reserva.idReserva && asi == asiento) encontrado = v.key ?: ""
            }
            encontrado
        } catch (_: Exception) { "" }
    }

    fun cambiarPuntoSubida(reserva: ReservaAdmin, nuevoPuntoSubida: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val path = "viajes/${reserva.ruta}/${reserva.fecha}/${reserva.horario}/reservas/${reserva.idReserva}/puntoSubida"
                db.reference(path).setValue(nuevoPuntoSubida)

                // Actualizar la lista local de reservas activas y pasadas
                _reservasActivas.value = _reservasActivas.value.map {
                    if (it.idReserva == reserva.idReserva && it.ruta == reserva.ruta && it.fecha == reserva.fecha)
                        it.copy(puntoSubida = nuevoPuntoSubida)
                    else it
                }
                _reservasPasadas.value = _reservasPasadas.value.map {
                    if (it.idReserva == reserva.idReserva && it.ruta == reserva.ruta && it.fecha == reserva.fecha)
                        it.copy(puntoSubida = nuevoPuntoSubida)
                    else it
                }
                // Actualizar también los resultados de búsqueda
                _resultadosBusqueda.value = _resultadosBusqueda.value.map {
                    if (it.reserva.idReserva == reserva.idReserva && it.reserva.ruta == reserva.ruta && it.reserva.fecha == reserva.fecha)
                        it.copy(puntoSubida = nuevoPuntoSubida, reserva = it.reserva.copy(puntoSubida = nuevoPuntoSubida))
                    else it
                }
                onResult(true)
            } catch (e: Exception) {
                onResult(false)
            }
        }
    }

    private val _precios = MutableStateFlow<Map<String, Int>>(mapOf("base" to 350, "maleta" to 150, "mascota" to 150))
    val precios: StateFlow<Map<String, Int>> = _precios.asStateFlow()

    init {
        loadCatalogs()
        cargarEstadisticasGlobales()
        cargarUsuarios()
        cargarPrecios()
        cargarConfigPagos()
        cargarGpsChoferes()
    }

    fun actualizarRango(inicio: String, fin: String) {
        fechaDesde.value = inicio
        fechaHasta.value = fin
        cargarEstadisticasGlobales()
    }

    private fun loadCatalogs() = viewModelScope.launch {
        // Sin defaults: lo que el admin borra se queda en blanco (no se auto-rellena).
        _ciudades.value = catalogRepository.getCatalog("ciudades")
        _horarios.value = catalogRepository.getCatalog("horarios")
        _paradas.value = catalogRepository.getCatalog("paradas")
        _puntosSubida.value = catalogRepository.getCatalog("puntosSubida")
        _vehiculos.value = catalogRepository.getCatalog("vehiculos")
        // Cargar info enriquecida de paradas (con ciudad y coordenadas)
        try {
            val db = network.FirebaseClient.database
            val snap = db.reference("catalogos/paradasInfo").valueEvents.first()
            if (snap.exists) {
                val lista = mutableListOf<ParadaInfo>()
                snap.children.forEach { child ->
                    val nombre = child.key ?: return@forEach
                    val ciudad = try { child.child("ciudad").value<String>() } catch (e: Exception) { "" }
                    val lat = try { child.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                    val lng = try { child.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                    val dias = try { child.child("dias").value<String>() } catch (e: Exception) { "" }
                    lista.add(ParadaInfo(nombre = nombre, ciudad = ciudad, lat = lat, lng = lng, dias = dias))
                }
                _paradasInfo.value = lista
            }
        } catch (_: Exception) {}
        // Cargar info enriquecida de puntos de subida
        try {
            val db2 = network.FirebaseClient.database
            val snap2 = db2.reference("catalogos/puntosSubidaInfo").valueEvents.first()
            if (snap2.exists) {
                val lista2 = mutableListOf<ParadaInfo>()
                snap2.children.forEach { child ->
                    val nombre = child.key ?: return@forEach
                    val ciudad = try { child.child("ciudad").value<String>() } catch (e: Exception) { "" }
                    val lat = try { child.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                    val lng = try { child.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                    val hora = try { child.child("hora").value<String>() } catch (e: Exception) { "" }
                    val dias = try { child.child("dias").value<String>() } catch (e: Exception) { "" }
                    lista2.add(ParadaInfo(nombre = nombre, ciudad = ciudad, lat = lat, lng = lng, hora = hora, dias = dias))
                }
                _puntosSubidaInfo.value = lista2
            }
        } catch (_: Exception) {}
    }

    /** Normaliza para comparar: minúsculas, sin acentos, sin espacios extremos.
     *  Evita duplicados tipo "Querétaro" vs "queretaro" vs "Queretaro". */
    private fun normalizar(texto: String): String {
        return texto.trim().lowercase()
            .replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u')
            .replace('ä', 'a').replace('ë', 'e').replace('ï', 'i').replace('ö', 'o').replace('ü', 'u')
            .replace('ñ', 'n')
    }

    fun agregarDato(categoria: String, nuevoDato: String, listaActual: List<String>) {
        val limpio = nuevoDato.trim()
        if (limpio.isBlank()) return
        if (listaActual.any { normalizar(it) == normalizar(limpio) }) {
            _mensajeGlobal.value = "\"$limpio\" ya existe (revisa mayúsculas/acentos)."
            return
        }
        val nuevaLista = listaActual + limpio
        viewModelScope.launch {
            catalogRepository.saveCatalog(categoria, nuevaLista)
            loadCatalogs()
        }
    }

    /**
     * Rechaza coordenadas fuera de México. Un punto de subida/bajada jamás cae fuera del país,
     * así que esto atrapa tanto una mala extracción del link como un dedazo al capturar a mano.
     * (0,0) = sin coordenadas, se permite.
     */
    private fun coordsFueraDeMexico(lat: Double, lng: Double): Boolean {
        if (lat == 0.0 && lng == 0.0) return false
        return lat !in 14.0..33.0 || lng !in -118.0..-86.0
    }

    private fun rechazaCoords(nombre: String, lat: Double, lng: Double): Boolean {
        if (coordsFueraDeMexico(lat, lng)) {
            _mensajeGlobal.value = "Coordenadas inválidas para \"$nombre\" ($lat, $lng): están fuera de México. " +
                "Revisa el link de Google Maps o captúralas de nuevo."
            return true
        }
        return false
    }

    fun guardarParadaConInfo(nombre: String, ciudad: String, lat: Double, lng: Double, listaActual: List<String>, dias: String = TipoDia.TODOS) {
        if (bloqueadoPorVersion()) return
        if (rechazaCoords(nombre, lat, lng)) return
        val limpio = nombre.trim()
        if (limpio.isBlank()) return
        if (listaActual.any { normalizar(it) == normalizar(limpio) }) {
            _mensajeGlobal.value = "La parada \"$limpio\" ya existe (revisa mayúsculas/acentos)."
            return
        }
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                // Guardar en lista simple para compatibilidad
                val nuevaLista = listaActual + limpio
                catalogRepository.saveCatalog("paradas", nuevaLista)
                // Guardar info enriquecida
                val info = mapOf("ciudad" to ciudad, "lat" to lat, "lng" to lng, "dias" to TipoDia.normalizar(dias))
                db.reference("catalogos/paradasInfo/$limpio").setValue(info)
                auditarCliente("catalogo_parada_alta", detalle = "Creó parada \"$limpio\" (ciudad: $ciudad, días: ${TipoDia.etiqueta(dias)}, coords: $lat, $lng)")
                loadCatalogs()
                _mensajeGlobal.value = if (lat == 0.0 && lng == 0.0)
                    "Parada guardada SIN ubicación en mapa — el boleto no mostrará el pin. Agrega coordenadas cuando puedas."
                else "Parada \"$limpio\" guardada."
            } catch (_: Exception) {}
        }
    }

    fun guardarPuntoSubidaConInfo(nombre: String, ciudad: String, lat: Double, lng: Double, hora: String, listaActual: List<String>, dias: String = TipoDia.TODOS) {
        if (bloqueadoPorVersion()) return
        if (rechazaCoords(nombre, lat, lng)) return
        val limpio = nombre.trim()
        if (limpio.isBlank()) return
        if (listaActual.any { normalizar(it) == normalizar(limpio) }) {
            _mensajeGlobal.value = "El punto de subida \"$limpio\" ya existe (revisa mayúsculas/acentos)."
            return
        }
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val nuevaLista = listaActual + limpio
                catalogRepository.saveCatalog("puntosSubida", nuevaLista)
                val info = mapOf("ciudad" to ciudad, "lat" to lat, "lng" to lng, "hora" to hora, "dias" to TipoDia.normalizar(dias))
                db.reference("catalogos/puntosSubidaInfo/$limpio").setValue(info)
                auditarCliente("catalogo_subida_alta", detalle = "Creó punto de subida \"$limpio\" (ciudad: $ciudad, hora: $hora, días: ${TipoDia.etiqueta(dias)}, coords: $lat, $lng)")
                loadCatalogs()
                _mensajeGlobal.value = if (lat == 0.0 && lng == 0.0)
                    "Punto de subida guardado SIN ubicación en mapa — el boleto no mostrará el pin. Agrega coordenadas cuando puedas."
                else "Punto de subida \"$limpio\" guardado."
            } catch (_: Exception) {}
        }
    }

    fun editarDato(categoria: String, datoViejo: String, datoNuevo: String, listaActual: List<String>) {
        if (datoNuevo.isNotBlank()) {
            val nuevaLista = listaActual.map { if (it == datoViejo) datoNuevo else it }
            viewModelScope.launch {
                catalogRepository.saveCatalog(categoria, nuevaLista)
                loadCatalogs()
            }
        }
    }

    fun editarParadaConInfo(nombreViejo: String, nombreNuevo: String, ciudad: String, lat: Double, lng: Double, listaActual: List<String>, dias: String = TipoDia.TODOS) {
        if (bloqueadoPorVersion()) return
        if (rechazaCoords(nombreNuevo, lat, lng)) return
        if (nombreNuevo.isNotBlank()) {
            viewModelScope.launch {
                try {
                    val db = network.FirebaseClient.database
                    // Si cambió el nombre, actualizar lista y borrar nodo viejo
                    if (nombreViejo != nombreNuevo) {
                        val nuevaLista = listaActual.map { if (it == nombreViejo) nombreNuevo else it }
                        catalogRepository.saveCatalog("paradas", nuevaLista)
                        db.reference("catalogos/paradasInfo/$nombreViejo").removeValue()
                    }
                    // Guardar/actualizar info enriquecida
                    val info = mapOf("ciudad" to ciudad, "lat" to lat, "lng" to lng, "dias" to TipoDia.normalizar(dias))
                    db.reference("catalogos/paradasInfo/$nombreNuevo").setValue(info)
                    auditarCliente("catalogo_parada_edicion", detalle = "Editó parada \"$nombreViejo\" -> \"$nombreNuevo\" (ciudad: $ciudad, días: ${TipoDia.etiqueta(dias)}, coords: $lat, $lng)")
                    loadCatalogs()
                } catch (_: Exception) {}
            }
        }
    }

    fun editarPuntoSubidaConInfo(nombreViejo: String, nombreNuevo: String, ciudad: String, lat: Double, lng: Double, hora: String, listaActual: List<String>, dias: String = TipoDia.TODOS) {
        if (bloqueadoPorVersion()) return
        if (rechazaCoords(nombreNuevo, lat, lng)) return
        if (nombreNuevo.isNotBlank()) {
            viewModelScope.launch {
                try {
                    val db = network.FirebaseClient.database
                    if (nombreViejo != nombreNuevo) {
                        val nuevaLista = listaActual.map { if (it == nombreViejo) nombreNuevo else it }
                        catalogRepository.saveCatalog("puntosSubida", nuevaLista)
                        db.reference("catalogos/puntosSubidaInfo/$nombreViejo").removeValue()
                    }
                    val info = mapOf("ciudad" to ciudad, "lat" to lat, "lng" to lng, "hora" to hora, "dias" to TipoDia.normalizar(dias))
                    db.reference("catalogos/puntosSubidaInfo/$nombreNuevo").setValue(info)
                    auditarCliente("catalogo_subida_edicion", detalle = "Editó punto de subida \"$nombreViejo\" -> \"$nombreNuevo\" (ciudad: $ciudad, hora: $hora, días: ${TipoDia.etiqueta(dias)}, coords: $lat, $lng)")
                    loadCatalogs()
                } catch (_: Exception) {}
            }
        }
    }

    fun eliminarDato(categoria: String, datoBorrar: String, listaActual: List<String>) {
        if (bloqueadoPorVersion()) return
        val nuevaLista = listaActual.filter { it != datoBorrar }
        viewModelScope.launch {
            catalogRepository.saveCatalog(categoria, nuevaLista)
            // Borrar TAMBIÉN el nodo enriquecido (coords/ciudad). Antes quedaba huérfano y el
            // punto seguía apareciendo en "Programar viaje" (que lee de *Info, no de la lista plana).
            try {
                val db = network.FirebaseClient.database
                when (categoria) {
                    "puntosSubida" -> db.reference("catalogos/puntosSubidaInfo/$datoBorrar").removeValue()
                    "paradas" -> db.reference("catalogos/paradasInfo/$datoBorrar").removeValue()
                }
            } catch (_: Exception) { /* best effort */ }
            loadCatalogs()
        }
    }

    // ═══ Vehículos con layout ═══

    fun cargarVehiculosConfig() {
        viewModelScope.launch {
            _vehiculosConfig.value = bookingRepository.obtenerVehiculosConfig()
        }
    }

    fun guardarVehiculoConfig(vehiculo: VehiculoConfig, onResult: (Boolean) -> Unit = {}) {
        viewModelScope.launch {
            val exito = bookingRepository.guardarVehiculoConfig(vehiculo)
            if (exito) {
                cargarVehiculosConfig()
                // También actualizar catálogo plano para compatibilidad con el dropdown de programar viaje
                val lista = _vehiculos.value.toMutableList()
                if (!lista.contains(vehiculo.nombre)) {
                    lista.add(vehiculo.nombre)
                    catalogRepository.saveCatalog("vehiculos", lista)
                    loadCatalogs()
                }
            }
            onResult(exito)
        }
    }

    fun editarVehiculoConfig(nombreViejo: String, vehiculo: VehiculoConfig, onResult: (Boolean) -> Unit = {}) {
        viewModelScope.launch {
            val exito = bookingRepository.editarVehiculoConfig(nombreViejo, vehiculo)
            if (exito) {
                cargarVehiculosConfig()
                loadCatalogs()
                cargarItinerarios()
            }
            onResult(exito)
        }
    }

    fun eliminarVehiculoConfig(vehiculoId: String, onResult: (Boolean) -> Unit = {}) {
        viewModelScope.launch {
            val exito = bookingRepository.eliminarVehiculoConfig(vehiculoId)
            if (exito) cargarVehiculosConfig()
            onResult(exito)
        }
    }

    fun cargarLayoutDeViaje(ruta: String, fecha: String, horario: String) {
        viewModelScope.launch {
            _layoutViaje.value = bookingRepository.obtenerLayoutDeViaje(ruta, fecha, horario)
        }
    }

    fun cargarEstadisticasGlobales() {
        viewModelScope.launch {
            _isLoading.value = true

            // FIX #11: Pasar rango de fechas para skip-early en iteración
            // Incluir el mes actual completo para calcular ingresosMes
            val mesActual = org.example.abigail.obtenerFechaHoy().dropLast(2) // "2026-05-"
            val inicioMes = mesActual + "01"
            val finMes = mesActual + "31"
            val rangoDesde = if (fechaDesde.value < inicioMes) fechaDesde.value else inicioMes
            val rangoHasta = if (fechaHasta.value > finMes) fechaHasta.value else finMes

            val historialGlobal = bookingRepository.getTodoElHistorial("viajes", rangoDesde, rangoHasta)

            var totalIngresosRango = 0
            var totalPendienteRango = 0
            val todasLasReservasDelRango = mutableListOf<Boleto>()
            val ingresosPorRuta = mutableMapOf<String, Int>()
            var totalMes = 0

            // Efectivo aún no cobrado no cuenta como ingreso confirmado
            fun esPendiente(b: Boleto) = b.metodoPago == "efectivo" && !b.pagoConfirmado

            historialGlobal.forEach { (nombreRuta, diasDeRuta) ->
                var ingresosDeEstaRuta = 0
                diasDeRuta.forEach { (fechaViaje, horariosDelDia) ->
                    // Acumular ingresos del rango seleccionado (confirmado vs pendiente)
                    if (fechaViaje >= fechaDesde.value && fechaViaje <= fechaHasta.value) {
                        horariosDelDia.forEach { (_, datosDia) ->
                            val reservas = datosDia.reservas.values
                            val confirmado = reservas.filterNot { esPendiente(it) }.sumOf { it.totalPagado }
                            val pendiente = reservas.filter { esPendiente(it) }.sumOf { it.totalPagado }
                            totalIngresosRango += confirmado
                            totalPendienteRango += pendiente
                            ingresosDeEstaRuta += confirmado
                            todasLasReservasDelRango.addAll(reservas)
                        }
                    }
                    // Acumular ingresos del mes actual (solo confirmado)
                    if (fechaViaje.startsWith(mesActual)) {
                        horariosDelDia.forEach { (_, datosDia) ->
                            totalMes += datosDia.reservas.values.filterNot { esPendiente(it) }.sumOf { it.totalPagado }
                        }
                    }
                }
                if (ingresosDeEstaRuta > 0) {
                    ingresosPorRuta[nombreRuta] = ingresosDeEstaRuta
                }
            }

            _boletosVendidos.value = todasLasReservasDelRango.sumOf { it.asientos.size }

            val mejorRuta = ingresosPorRuta.maxByOrNull { it.value }
            if (mejorRuta != null) {
                _rutaMasPopular.value = "${mejorRuta.key} ($${mejorRuta.value})"
            } else {
                _rutaMasPopular.value = "Sin ingresos en este rango"
            }

            _ingresosSemana.value = totalIngresosRango
            _ingresosMes.value = totalMes
            _ingresosPendientes.value = totalPendienteRango

            _bookings.value =
                todasLasReservasDelRango.sortedBy { it.fechaViaje }
            val nuevasAlertas = mutableListOf<AlertaActividad>()

            val ultimasCompras =
                todasLasReservasDelRango.sortedByDescending { it.timestampLong }.take(2)
            ultimasCompras.forEach { boleto ->
                nuevasAlertas.add(
                    AlertaActividad(
                        icono = "TICKET",
                        titulo = "Nueva Reserva",
                        descripcion = "Se vendieron ${boleto.asientos.size} asientos para la ruta ${boleto.origenDestino}. Total: $${boleto.totalPagado}",
                        color = androidx.compose.ui.graphics.Color(0xFF4CAF50)
                    )
                )
            }
            nuevasAlertas.add(
                AlertaActividad(
                    icono = "ALERTA",
                    titulo = "Aviso de Logística",
                    descripcion = "Verifica que tengas vehículos asignados para los viajes de mañana.",
                    color = androidx.compose.ui.graphics.Color(0xFFFF9800)
                )
            )

            _alertas.value = nuevasAlertas
            _isLoading.value = false
        }
    }

    /**
     * [repeticiones] solo lo usan las recurrencias por tipo de día ("Sábados" = cuántos
     * sábados, "Entre semana" = cuántas semanas L-V). Las demás lo ignoran.
     */
    fun publicarItinerario(origen: String,destino: String,fechaInicio: String,fechaFin: String,horario: String, chofer: String,vehiculo: String, recurrencia: String, puntoSubida: String = "", urlSalida: String = "", repeticiones: Int = 0) {
        viewModelScope.launch {
            // Faltaba el guard: una APK vieja podía seguir creando viajes.
            if (bloqueadoPorVersion()) return@launch
            // Un viaje necesita vehículo para definir el mapa de asientos del cliente
            if (vehiculo.isBlank()) {
                _mensajeGlobal.value = "Asigna un vehículo al viaje — define los asientos que verá el cliente."
                return@launch
            }
            // El nombre del vehículo del dropdown (catálogo 'vehiculos') debe existir en
            // vehiculos_config, que es de donde sale la capacidad. Si no coincide, el viaje
            // se creaba SIN capacidad y el cliente veía un mapa de 13 asientos por defecto,
            // sin importar la van real. Se aborta con mensaje en vez de crear un viaje fantasma.
            // Si la lista aún no cargó, no bloquear (evita falso negativo por datos sin cargar):
            // programarViaje resuelve por nombre igual, y el peor caso es el fallback previo.
            val config = _vehiculosConfig.value
            if (config.isNotEmpty() && config.none { it.nombre.trim() == vehiculo.trim() }) {
                _mensajeGlobal.value = "El vehículo \"$vehiculo\" no tiene configuración de asientos. " +
                    "Créalo o edítalo en Vehículos antes de programar el viaje."
                return@launch
            }
            val ruta = "$origen - $destino"
            // Resolver el uid del chofer por su nombre (vínculo robusto). Si no se encuentra,
            // queda solo por nombre (compatibilidad).
            val choferUid = _usuariosRegistrados.value.firstOrNull { it.nombre.trim().equals(chofer.trim(), ignoreCase = true) }?.uid ?: ""
            // "Sábados" y "Entre semana" no son rangos continuos: saltan días, así que
            // generan su propia lista de fechas en vez de contar días corridos.
            val fechas: List<String> = when (recurrencia) {
                "Sábados" -> sabadosDesde(fechaInicio, if (repeticiones > 0) repeticiones else 4)
                "Entre semana" -> diasEntreSemanaDesde(fechaInicio, if (repeticiones > 0) repeticiones else 1)
                else -> {
                    val diasAGenerar = when (recurrencia) {
                        "Único" -> 1
                        "Personalizado" -> calcularDiasEntre(fechaInicio, fechaFin)
                        "Semanal" -> 7
                        "Mensual" -> 30
                        "Indefinido" -> 30
                        else -> 1
                    }
                    (0 until diasAGenerar).map { org.example.abigail.calcularFechaFutura(fechaInicio, it) }
                }
            }
            if (fechas.isEmpty()) {
                _mensajeGlobal.value = "No se generó ninguna fecha para esa recurrencia. Revisa la fecha de inicio."
                return@launch
            }
            var creados = 0
            var duplicados = 0
            for (fechaDelViaje in fechas) {
                val exito = bookingRepository.programarViaje(ruta, fechaDelViaje, horario, chofer, vehiculo, puntoSubida, urlSalida, choferUid)
                if (exito) creados++ else duplicados++
            }
            if (creados > 0) {
                NotificacionViewModel.enviar(
                    tipo = "asignacion",
                    titulo = "Nuevo viaje asignado",
                    mensaje = "Se te asignó la ruta $origen - $destino desde ${fechas.first()} ($recurrencia) a las $horario. Vehículo: $vehiculo",
                    destinatarioRol = "chofer"
                )
                _mensajeGlobal.value = if (duplicados > 0)
                    "Se crearon $creados viaje(s). $duplicados ya existían y se omitieron."
                else
                    "Se crearon $creados viaje(s) correctamente."
            } else {
                _mensajeGlobal.value = "No se crearon viajes — todos ya existían para esa ruta/fecha/horario."
            }
        }
    }

    private fun calcularDiasEntre(fechaInicio: String, fechaFin: String): Int {
        if (fechaFin.isBlank()) return 1
        try {
            val partesInicio = fechaInicio.split("-")
            val partesFin = fechaFin.split("-")

            val anioI = partesInicio[0].toInt()
            val mesI = partesInicio[1].toInt()
            val diaI = partesInicio[2].toInt()

            val anioF = partesFin[0].toInt()
            val mesF = partesFin[1].toInt()
            val diaF = partesFin[2].toInt()

            var dias = 0
            var anio = anioI; var mes = mesI; var dia = diaI

            while (dias < 365) {
                if (anio == anioF && mes == mesF && dia == diaF) {
                    return dias + 1
                }
                dias++
                dia++
                val diasEnMes = when (mes) {
                    1, 3, 5, 7, 8, 10, 12 -> 31
                    4, 6, 9, 11 -> 30
                    2 -> if (anio % 4 == 0 && (anio % 100 != 0 || anio % 400 == 0)) 29 else 28
                    else -> 30
                }
                if (dia > diasEnMes) {
                    dia = 1
                    mes++
                    if (mes > 12) { mes = 1; anio++ }
                }
            }
            return 1
        } catch (e: Exception) {
            return 1
        }
    }


    fun cargarUsuarios() {
        viewModelScope.launch {
            val usuariosBD = userRepository.getUsuariosApp()
            _usuariosRegistrados.value = usuariosBD
            _choferes.value = usuariosBD.filter { it.rol == "chofer" }.map { it.nombre }
        }
    }

    /** Registra un movimiento en la bitácora de auditoría desde el cliente (admin autenticado). */
    private suspend fun auditarCliente(accion: String, targetUid: String = "", detalle: String = "") {
        try {
            val db = network.FirebaseClient.database
            val uid = dev.gitlive.firebase.Firebase.auth.currentUser?.uid ?: ""
            val nombre = try { db.reference("usuarios/$uid/nombre").valueEvents.first().value<String>() } catch (e: Exception) { "" }
            val id = db.reference("auditoria").push().key ?: return
            db.reference("auditoria/$id").setValue(mapOf(
                "accion" to accion,
                "operadorUid" to uid,
                "operadorNombre" to nombre,
                "targetUid" to targetUid,
                "detalle" to detalle,
                "timestamp" to org.example.abigail.obtenerMilisegundosActuales().toString()
            ))
        } catch (e: Exception) { /* best effort */ }
    }

    fun cambiarRol(uid: String, nuevoRol: String, miRolActual: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            if (miRolActual == "desarrollador" || miRolActual == "admin_general" || miRolActual == "master") {
                // Capturar nombre + rol ANTERIOR del afectado antes del cambio, para dejar
                // rastro completo en auditoría ("Cambió a Fulano de chofer a admin").
                val afectado = usuariosRegistrados.value.firstOrNull { it.uid == uid }
                val nombreAfectado = afectado?.nombre?.ifBlank { afectado.correo } ?: uid
                val rolAnterior = afectado?.rol ?: "desconocido"
                val exito = userRepository.actualizarRolUsuario(uid, nuevoRol)
                if (exito) {
                    userRepository.limpiarCambioPendiente(uid)
                    auditarCliente("rol_cambiado", uid,
                        "Cambió a $nombreAfectado de rol '$rolAnterior' a '$nuevoRol'")
                    cargarUsuarios()
                }
            } else if (miRolActual == "admin") {
                val miUid = dev.gitlive.firebase.Firebase.auth.currentUser?.uid ?: "AdminDesconocido"
                val exito = userRepository.solicitarCambioRol(uid, nuevoRol, miUid)
                if (exito) {
                    cargarUsuarios()
                    NotificacionViewModel.enviar(
                        tipo = "rol",
                        titulo = "Solicitud de cambio de rol",
                        mensaje = "Se ha solicitado cambiar el rol del usuario a $nuevoRol. Requiere aprobación de Admin General.",
                        destinatarioRol = "admin_general"
                    )
                }
            }
        }
    }

    fun confirmarCambio(uidUsuario: String, rolAprobado: String) {
        viewModelScope.launch {
            val exito = userRepository.aprobarCambioRol(uidUsuario, rolAprobado)
            if (exito) {
                auditarCliente("rol_cambiado", uidUsuario, "Aprobó cambio de rol a $rolAprobado")
                cargarUsuarios()
            }
        }
    }

    fun cancelarCambio(uidUsuario: String) {
        viewModelScope.launch {
            val exito = userRepository.declinarCambioRol(uidUsuario)
            if (exito) cargarUsuarios()
        }
    }

    fun cancelarViaje(ruta: String, fecha: String, horario: String) {
        viewModelScope.launch {
            try {
                // Vía Cloud Function: avisa por correo a los pasajeros afectados, borra el viaje
                // y deja rastro en auditoría.
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(horario))
                    })
                }
                val response = client.post(network.CloudFunctions.CANCELAR_VIAJE_AVISO) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val txt = response.body<String>()
                if (txt.contains("\"success\":true") || txt.contains("\"success\": true")) {
                    cargarItinerarios()
                    val avisados = Regex("\"avisados\"\\s*:\\s*(\\d+)").find(txt)?.groupValues?.getOrNull(1)?.toIntOrNull() ?: 0
                    val vendidos = Regex("\"vendidos\"\\s*:\\s*(\\d+)").find(txt)?.groupValues?.getOrNull(1)?.toIntOrNull() ?: 0
                    _mensajeGlobal.value = if (vendidos > 0)
                        "Viaje cancelado. Se avisó por correo a $avisados pasajero(s). Gestiona el reembolso de $vendidos asiento(s) vendido(s)."
                    else
                        "Viaje cancelado: $ruta ($fecha $horario)"
                } else {
                    _mensajeGlobal.value = "Error al cancelar el viaje. Intenta de nuevo."
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.cancelarViaje")
                _mensajeGlobal.value = "Error al cancelar el viaje: ${e.message}"
            }
        }
    }

    suspend fun verificarAsientosVendidos(ruta: String, fecha: String, horario: String): Int {
        return bookingRepository.contarAsientosOcupados(ruta, fecha, horario)
    }

    fun cargarGestionDeBoletos() {
        viewModelScope.launch {
            val fechaHoyTexto = org.example.abigail.obtenerFechaHoy()
            val todasLasReservas = bookingRepository.obtenerTodasLasReservasAdmin(fechaHoyTexto)
            _reservasPasadas.value = todasLasReservas.filter { it.esPasado }
            _reservasActivas.value = todasLasReservas.filter { !it.esPasado }
        }
    }

    fun reprogramarViaje(ruta: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, nuevaFecha: String, nuevoHorario: String) {
        viewModelScope.launch {
            val exito = bookingRepository.reprogramarReserva(ruta, fechaAntigua, horarioAntiguo, idReserva, nuevaFecha, nuevoHorario)
            if (exito) cargarGestionDeBoletos()
        }
    }

    suspend fun obtenerHorariosParaFecha(ruta: String, fecha: String): List<String> {
        return bookingRepository.getHorariosProgramados(ruta, fecha)
    }

    suspend fun buscarAsientosOcupados(ruta: String, fecha: String, horario: String): List<Int> {
        return bookingRepository.obtenerListaAsientosOcupados(ruta, fecha, horario)
    }

    fun ejecutarTraspasoDefinitivo(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val exito = bookingRepository.reprogramarReservaCompleta(rutaAntigua, fechaAntigua, horarioAntiguo, idReserva, rutaNueva, fechaNueva, horarioNuevo, nuevosAsientos)
            if (exito) cargarGestionDeBoletos()
            else _mensajeGlobal.value = "No se pudo reprogramar: uno o más asientos destino ya están ocupados. Elige otros."
            onResult(exito)
        }
    }

    fun ejecutarDivisionDeGrupo(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReservaOriginal: String, pasajerosAMover: List<PasajeroInfo>, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val exito = bookingRepository.dividirYReprogramarGrupo(rutaAntigua, fechaAntigua, horarioAntiguo, idReservaOriginal, pasajerosAMover, rutaNueva, fechaNueva, horarioNuevo, nuevosAsientos)
            if (exito) cargarGestionDeBoletos()
            else _mensajeGlobal.value = "No se pudo dividir el grupo: uno o más asientos destino ya están ocupados. Elige otros."
            onResult(exito)
        }
    }

    fun escucharAsientosEnVivo(ruta: String, fecha: String, horario: String): Flow<List<Int>> {
        return bookingRepository.observarAsientosNoDisponibles(ruta, fecha, horario, idSesionActual)
    }

    suspend fun intentarSeleccionar(ruta: String, fecha: String, horario: String, asiento: Int): Boolean {
        val limite = bookingRepository.bloquearAsientoTemporal(ruta, fecha, horario, asiento, idSesionActual)

        return if (limite != null) {
            iniciarCuentaRegresiva(limite)
            true
        } else {
            false
        }
    }

    fun liberarAsiento(ruta: String, fecha: String, horario: String, asiento: Int) {
        viewModelScope.launch { bookingRepository.liberarAsientoBloqueado(ruta, fecha, horario, asiento) }
    }

    fun iniciarCuentaRegresiva(tiempoFinal: Long) {
        timerJob?.cancel()
        _mostrarTemporizador.value = true
        timerJob = viewModelScope.launch {
            while (tiempoFinal > org.example.abigail.obtenerMilisegundosActuales()) {
                _tiempoRestanteMilis.value = tiempoFinal - org.example.abigail.obtenerMilisegundosActuales()
                delay(1000)
            }
            finalizarSesionPorTiempo()
        }
    }

    fun finalizarSesionPorTiempo() {
        _mostrarTemporizador.value = false
        _tiempoRestanteMilis.value = 0L
    }

    fun ejecutarReasignacionMasiva(choferSaliente: String, choferEntrante: String, vehiculoNuevo: String) {
        viewModelScope.launch {
            _isLoading.value = true
            val fechaHoy = org.example.abigail.obtenerFechaHoy()

            // El vehículo nuevo NO puede tener menos asientos que el que se reemplaza: si no,
            // los viajes quedarían con menos lugares que los ya vendidos (sobreventa/asientos
            // fantasma). Se exige capacidad igual o mayor.
            val capNueva = _vehiculosConfig.value.firstOrNull { it.nombre.trim() == vehiculoNuevo.trim() }?.capacidad ?: 0
            val capRequerida = try {
                val db = network.FirebaseClient.database
                val snap = db.reference("viajes_programados").valueEvents.first()
                var maxCap = 0
                if (snap.exists) snap.children.forEach { rutaSnap ->
                    rutaSnap.children.forEach { fechaSnap ->
                        if ((fechaSnap.key ?: "") >= fechaHoy) fechaSnap.children.forEach { hSnap ->
                            val ch = try { hSnap.child("chofer").value<String>() } catch (_: Exception) { "" }
                            if (ch == choferSaliente) {
                                val c = try { hSnap.child("capacidad").value<Int>() } catch (_: Exception) { 0 }
                                if (c > maxCap) maxCap = c
                            }
                        }
                    }
                }
                maxCap
            } catch (_: Exception) { 0 }

            if (capNueva == 0) {
                _isLoading.value = false
                _mensajeGlobal.value = "El vehículo \"$vehiculoNuevo\" no tiene configuración de asientos."
                return@launch
            }
            if (capRequerida > 0 && capNueva < capRequerida) {
                _isLoading.value = false
                _mensajeGlobal.value = "El vehículo \"$vehiculoNuevo\" ($capNueva asientos) tiene menos lugares que " +
                    "los viajes a reasignar ($capRequerida). Elige uno de igual o mayor capacidad."
                return@launch
            }

            val modificados = bookingRepository.reasignarLogisticaMasiva(choferSaliente, choferEntrante, vehiculoNuevo, fechaHoy, uidDeChofer(choferEntrante))

            if (modificados > 0) {
                _mensajeReasignacion.value = "¡Logística Salvada! Se reasignaron $modificados viajes correctamente."
            } else {
                _mensajeReasignacion.value = "No se encontraron viajes futuros asignados a este chofer."
            }

            cargarItinerarios()
            _isLoading.value = false
        }
    }
    fun cargarItinerarios() {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val snapshot = db.reference("viajes_programados").valueEvents.first()
                if (!snapshot.exists) {
                    _itinerarios.value = emptyList()
                    return@launch
                }
                val listaTemp = mutableListOf<ViajeActivo>()
                // FIX #17: Solo cargar itinerarios de hoy en adelante (no históricos)
                val fechaHoy = org.example.abigail.obtenerFechaHoy()

                snapshot.children.forEach { rutaSnap ->
                    val ruta = rutaSnap.key ?: return@forEach
                    rutaSnap.children.forEach { fechaSnap ->
                        val fecha = fechaSnap.key ?: return@forEach
                        // Skip fechas pasadas — no son relevantes para itinerarios
                        if (fecha < fechaHoy) return@forEach
                        fechaSnap.children.forEach { horarioSnap ->
                            val horario = horarioSnap.key ?: return@forEach
                            val chofer = try { horarioSnap.child("chofer").value<String>() } catch (e: Exception) { "Sin asignar" }
                            val vehiculo = try { horarioSnap.child("vehiculo").value<String>() } catch (e: Exception) { "Sin asignar" }
                            val capacidad = try { horarioSnap.child("capacidad").value<Int>() } catch (e: Exception) { 0 }
                            val vendidos = try {
                                val ocSnap = db.reference("viajes/$ruta/$fecha/$horario/ocupados").valueEvents.first()
                                if (ocSnap.exists) ocSnap.children.count() else 0
                            } catch (e: Exception) { 0 }
                            listaTemp.add(
                                ViajeActivo(
                                    ruta = ruta,
                                    fecha = fecha,
                                    horario = horario,
                                    chofer = chofer,
                                    vehiculo = vehiculo,
                                    vendidos = vendidos,
                                    capacidad = capacidad
                                )
                            )
                        }
                    }
                }
                _itinerarios.value = listaTemp
            } catch (e: Exception) {
                _itinerarios.value = emptyList()
            }
        }
    }

    fun limpiarMensajeReasignacion() {
        _mensajeReasignacion.value = null
    }

    fun forzarCambioPendiente(uid: String, nuevoRol: String) {
        viewModelScope.launch {
            val exito = userRepository.ejecutarRolPendiente(uid, nuevoRol)
            if (exito) cargarUsuarios()
        }
    }

    private fun cargarPrecios() {
        viewModelScope.launch {
            try {
                _precios.value = catalogRepository.getPrecios()
            } catch (e: Exception) {
                _precios.value = mapOf("base" to 350, "maleta" to 150, "mascota" to 150)
            }
        }
    }

    /** Bloqueo por versión desactualizada: evita que un cliente viejo escriba con esquema
     *  obsoleto tras un bump de minVersion. Devuelve true si está bloqueado (y avisa). */
    private fun bloqueadoPorVersion(): Boolean {
        if (network.VersionGate.bloqueado) {
            _mensajeGlobal.value = "Actualiza la app para poder hacer cambios. Estás en modo solo lectura."
            return true
        }
        return false
    }

    fun guardarPrecios(nuevosPrecios: Map<String, Int>) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            // Validación de cordura: precios positivos y dentro de límites razonables.
            // El servidor además rechaza cualquier transacción mayor a $50,000 MXN.
            val base = nuevosPrecios["base"] ?: 0
            val maleta = nuevosPrecios["maleta"] ?: 0
            val mascota = nuevosPrecios["mascota"] ?: 0
            // costoServicio SÍ puede ser 0 (admin decide no cobrar recargo por Stripe).
            val costoServicio = nuevosPrecios["costoServicio"] ?: CalculoPrecios.COSTO_SERVICIO_STRIPE
            if (base <= 0 || maleta < 0 || mascota < 0 || costoServicio < 0) {
                _mensajeGlobal.value = "El precio base debe ser mayor a 0 y los demás no pueden ser negativos."
                return@launch
            }
            if (base > 10000 || maleta > 5000 || mascota > 5000) {
                _mensajeGlobal.value = "Precio fuera de rango. Máximo: base \$10,000 · maleta/mascota \$5,000."
                return@launch
            }
            if (costoServicio > 500) {
                _mensajeGlobal.value = "El costo por servicio no puede superar \$500 MXN."
                return@launch
            }
            val exito = catalogRepository.savePrecios(nuevosPrecios)
            if (exito) {
                _precios.value = nuevosPrecios
                _mensajeGlobal.value = "Precios actualizados correctamente."
            } else {
                _mensajeGlobal.value = "Error al guardar los precios."
            }
        }
    }

    fun crearCuenta(email: String, password: String, nombre: String, rol: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            val exito = userRepository.crearCuenta(email, password, nombre, rol)
            if (exito) {
                cargarUsuarios()
                _mensajeGlobal.value = "Cuenta creada exitosamente para $nombre ($email)"
            } else {
                _mensajeGlobal.value = "Error al crear la cuenta. Verifica que el correo no esté ya registrado."
            }
        }
    }

    private val _resetResult = MutableStateFlow<Pair<String, String>?>(null)
    val resetResult: StateFlow<Pair<String, String>?> = _resetResult.asStateFlow()

    fun resetearContrasena(uid: String, nombreUsuario: String) {
        viewModelScope.launch {
            val adminUid = dev.gitlive.firebase.Firebase.auth.currentUser?.uid ?: return@launch
            val tempPassword = userRepository.resetearContrasena(uid, adminUid)
            if (tempPassword != null) {
                _resetResult.value = Pair(nombreUsuario, tempPassword)
            } else {
                _resetResult.value = Pair(nombreUsuario, "ERROR")
            }
        }
    }

    fun limpiarResetResult() {
        _resetResult.value = null
    }

    fun eliminarCuenta(uid: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            val exito = userRepository.eliminarCuenta(uid)
            if (exito) {
                cargarUsuarios()
                _mensajeGlobal.value = "Cuenta eliminada correctamente."
            } else {
                _mensajeGlobal.value = "Error al eliminar la cuenta."
            }
        }
    }
    fun verificarYEliminarCuenta(
        user: UsuarioApp,
        onTieneViajes: (Int) -> Unit,
        onEliminado: () -> Unit
    ) {
        viewModelScope.launch {
            if (user.rol == "chofer") {
                val viajesFuturos = contarViajesFuturosDelChofer(user.nombre)
                if (viajesFuturos > 0) {
                    onTieneViajes(viajesFuturos)
                    return@launch
                }
            }
            val exito = userRepository.eliminarCuenta(user.uid)
            if (exito) {
                cargarUsuarios()
                onEliminado()
            }
        }
    }

    private suspend fun contarViajesFuturosDelChofer(nombreChofer: String): Int {
        return try {
            val db = network.FirebaseClient.database
            val fechaHoy = org.example.abigail.obtenerFechaHoy()
            val snapshot = db.reference("viajes_programados").valueEvents.first()
            if (!snapshot.exists) return 0

            var contador = 0
            snapshot.children.forEach { rutaSnap ->
                rutaSnap.children.forEach { fechaSnap ->
                    val fecha = fechaSnap.key ?: ""
                    if (fecha >= fechaHoy) {
                        fechaSnap.children.forEach { horarioSnap ->
                            try {
                                val chofer = horarioSnap.child("chofer").value<String>()
                                if (chofer == nombreChofer) {
                                    contador++
                                }
                            } catch (e: Exception) {}
                        }
                    }
                }
            }
            contador
        } catch (e: Exception) {
            0
        }
    }

    fun reasignarYEliminarChofer(choferAEliminar: UsuarioApp, nuevoChofer: String) {
        viewModelScope.launch {
            // Reasignar primero (puede ser 0 viajes); luego eliminar SIEMPRE.
            val reasignados = reasignarViajesDeChofer(choferAEliminar.nombre, nuevoChofer)
            val exito = userRepository.eliminarCuenta(choferAEliminar.uid)
            if (exito) {
                cargarUsuarios()
                _mensajeGlobal.value = if (reasignados > 0)
                    "Chofer eliminado. Se reasignaron $reasignados viaje(s) a $nuevoChofer."
                else
                    "Chofer eliminado (no tenía viajes futuros que reasignar)."
            } else {
                _mensajeGlobal.value = "Error al eliminar la cuenta del chofer."
            }
        }
    }

    /** uid del chofer por nombre. Las rules exigen que choferUid coincida con auth.uid para que
     *  el chofer pueda escanear QR / marcar asistencia / enviar GPS de su viaje. */
    private fun uidDeChofer(nombre: String): String =
        _usuariosRegistrados.value.firstOrNull { it.nombre.trim().equals(nombre.trim(), ignoreCase = true) }?.uid ?: ""

    private suspend fun reasignarViajesDeChofer(choferViejo: String, choferNuevo: String): Int {
        return try {
            val db = network.FirebaseClient.database
            val fechaHoy = org.example.abigail.obtenerFechaHoy()
            val snapshot = db.reference("viajes_programados").valueEvents.first()
            if (!snapshot.exists) return 0

            var modificados = 0
            val updates = mutableMapOf<String, Any?>()
            snapshot.children.forEach { rutaSnap ->
                val ruta = rutaSnap.key ?: return@forEach
                rutaSnap.children.forEach { fechaSnap ->
                    val fecha = fechaSnap.key ?: return@forEach
                    if (fecha >= fechaHoy) {
                        fechaSnap.children.forEach { horarioSnap ->
                            val horario = horarioSnap.key ?: return@forEach
                            try {
                                val chofer = horarioSnap.child("chofer").value<String>()
                                if (chofer == choferViejo) {
                                    updates["viajes_programados/$ruta/$fecha/$horario/chofer"] = choferNuevo
                                    // Revincular por uid al nuevo chofer (null lo dejaría sin permiso
                                    // de escanear QR/asistencia/GPS si luego se define choferUid).
                                    updates["viajes_programados/$ruta/$fecha/$horario/choferUid"] =
                                        uidDeChofer(choferNuevo).ifBlank { null }
                                    modificados++
                                }
                            } catch (e: Exception) {}
                        }
                    }
                }
            }
            if (updates.isNotEmpty()) {
                db.reference().updateChildren(updates)
            }
            modificados
        } catch (e: Exception) {
            0
        }
    }
    fun cancelarBoleto(reserva: ReservaAdmin, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val exito = bookingRepository.cancelarReservaAdmin(
                reserva.ruta, reserva.fecha, reserva.horario, reserva.idReserva
            )
            if (exito) cargarGestionDeBoletos()
            onResult(exito)
        }
    }

    fun reenviarBoletos(reserva: ReservaAdmin, correoDestino: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                val client = network.sharedHttpClient
                val pasajerosJsonStr = PasajeroInfo.listaAJsonString(reserva.pasajeros)
                val db = network.FirebaseClient.database
                val puntoSubidaVal = try {
                    val snap = db.reference("viajes_programados/${reserva.ruta}/${reserva.fecha}/${reserva.horario}/puntoSubida").valueEvents.first()
                    if (snap.exists) snap.value<String>() else ""
                } catch (_: Exception) { "" }

                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("email", kotlinx.serialization.json.JsonPrimitive(correoDestino))
                        put("pasajeros", kotlinx.serialization.json.JsonPrimitive(pasajerosJsonStr))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(reserva.ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(reserva.fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(reserva.horario))
                        put("totalPagado", kotlinx.serialization.json.JsonPrimitive(reserva.totalPagado))
                        put("reservaId", kotlinx.serialization.json.JsonPrimitive(reserva.idReserva))
                        if (puntoSubidaVal.isNotBlank()) put("puntoSubida", kotlinx.serialization.json.JsonPrimitive(puntoSubidaVal))
                    })
                }
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                client.post(network.CloudFunctions.ENVIAR_BOLETOS) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                _mensajeGlobal.value = "Boletos reenviados a $correoDestino"
                onResult(true)
            } catch (e: Exception) {
                _mensajeGlobal.value = "Error al reenviar boletos: ${e.message}"
                onResult(false)
            }
        }
    }

    // ===== RESERVAS EN EFECTIVO =====

    fun reservarEnEfectivo(
        ruta: String,
        fecha: String,
        horario: String,
        pasajeros: List<PasajeroInfo>,
        totalPagado: Int,
        puntoSubida: String = "",
        emailTitular: String = "",
        onResult: (Boolean) -> Unit
    ) {
        viewModelScope.launch {
            try {
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: run {
                    _mensajeGlobal.value = "Error: No autenticado"
                    onResult(false)
                    return@launch
                }

                val asientosArray = kotlinx.serialization.json.buildJsonArray {
                    pasajeros.forEach { add(kotlinx.serialization.json.JsonPrimitive(it.asiento)) }
                }
                val pasajerosJsonStr = PasajeroInfo.listaAJsonString(pasajeros)
                val pasajerosElement = kotlinx.serialization.json.Json.parseToJsonElement(pasajerosJsonStr)

                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(horario))
                        put("asientos", asientosArray)
                        put("pasajeros", pasajerosElement)
                        put("totalPagado", kotlinx.serialization.json.JsonPrimitive(totalPagado))
                        if (puntoSubida.isNotBlank()) {
                            put("puntoSubida", kotlinx.serialization.json.JsonPrimitive(puntoSubida))
                        }
                        if (emailTitular.isNotBlank()) {
                            put("email", kotlinx.serialization.json.JsonPrimitive(emailTitular))
                        }
                    })
                }

                val client = network.sharedHttpClient
                val response = client.post(network.CloudFunctions.OCUPAR_ASIENTOS_EFECTIVO) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val responseText = response.body<String>()

                if (responseText.contains("\"success\":true") || responseText.contains("\"success\": true")) {
                    _mensajeGlobal.value = "Reserva en efectivo creada. Asientos: ${pasajeros.map { it.asiento }.joinToString(", ")}"
                    onResult(true)
                } else {
                    val errorMsg = if (responseText.contains("ya ocupados")) {
                        "Error: Uno o más asientos ya están ocupados por otro usuario."
                    } else {
                        "Error al crear la reserva. Intenta de nuevo."
                    }
                    _mensajeGlobal.value = errorMsg
                    onResult(false)
                }
            } catch (e: Exception) {
                _mensajeGlobal.value = "Error: ${e.message}"
                onResult(false)
            }
        }
    }

    fun confirmarPagoEfectivo(ruta: String, fecha: String, horario: String, idReserva: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                // Vía Cloud Function: firma confirmadoPor con el uid real del operador y deja
                // rastro en auditoría (no falsificable desde el cliente).
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(horario))
                        put("reservaId", kotlinx.serialization.json.JsonPrimitive(idReserva))
                    })
                }
                val response = client.post(network.CloudFunctions.CONFIRMAR_PAGO_EFECTIVO) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val txt = response.body<String>()
                if (txt.contains("\"success\":true") || txt.contains("\"success\": true")) {
                    _mensajeGlobal.value = "Pago en efectivo confirmado."
                    onResult(true)
                } else {
                    _mensajeGlobal.value = if (txt.contains("ya estaba confirmado")) "El pago ya estaba confirmado." else "Error al confirmar el pago en efectivo."
                    onResult(false)
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.confirmarPagoEfectivo[$idReserva]")
                _mensajeGlobal.value = "Error confirmando pago: ${e.message}"
                onResult(false)
            }
        }
    }

    fun obtenerPuntoSubida(ruta: String, fecha: String, horario: String, onResult: (String) -> Unit) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("viajes_programados/$ruta/$fecha/$horario/puntoSubida").valueEvents.first()
                if (snap.exists) {
                    onResult(snap.value<String>())
                } else {
                    onResult("")
                }
            } catch (e: Exception) {
                onResult("")
            }
        }
    }

    // ═══ Solicitudes de Factura ═══
    private val _solicitudesFactura = MutableStateFlow<List<SolicitudFactura>>(emptyList())
    val solicitudesFactura: StateFlow<List<SolicitudFactura>> = _solicitudesFactura.asStateFlow()

    private val _correoContador = MutableStateFlow("")
    val correoContador: StateFlow<String> = _correoContador.asStateFlow()

    private val _correoCopia = MutableStateFlow("")
    val correoCopia: StateFlow<String> = _correoCopia.asStateFlow()

    fun cargarCorreoContador() {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("catalogos/configuracion/correoContador").valueEvents.first()
                if (snap.exists) _correoContador.value = snap.value<String>()
                val snapCC = db.reference("catalogos/configuracion/correoCopia").valueEvents.first()
                if (snapCC.exists) _correoCopia.value = snapCC.value<String>()
            } catch (e: Exception) { /* usa vacío */ }
        }
    }

    fun guardarCorreoContador(correo: String) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                db.reference("catalogos/configuracion/correoContador").setValue(correo)
                _correoContador.value = correo
            } catch (e: Exception) { /* ignore */ }
        }
    }

    fun guardarCorreoCopia(correo: String) {
        viewModelScope.launch {
            try {
                val db = network.FirebaseClient.database
                db.reference("catalogos/configuracion/correoCopia").setValue(correo)
                _correoCopia.value = correo
            } catch (e: Exception) { /* ignore */ }
        }
    }

    fun cargarSolicitudesFactura() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val db = network.FirebaseClient.database
                val snap = db.reference("solicitudes_factura").valueEvents.first()
                if (snap.exists) {
                    val lista = mutableListOf<SolicitudFactura>()
                    snap.children.forEach { child ->
                        try {
                            lista.add(SolicitudFactura(
                                id = child.key ?: "",
                                rfc = try { child.child("rfc").value<String>() } catch (e: Exception) { "" },
                                razonSocial = try { child.child("razonSocial").value<String>() } catch (e: Exception) { "" },
                                regimenFiscal = try { child.child("regimenFiscal").value<String>() } catch (e: Exception) { "" },
                                usoCfdi = try { child.child("usoCfdi").value<String>() } catch (e: Exception) { "" },
                                cpFiscal = try { child.child("cpFiscal").value<String>() } catch (e: Exception) { "" },
                                email = try { child.child("email").value<String>() } catch (e: Exception) { "" },
                                monto = try { child.child("monto").value<Int>() } catch (e: Exception) { 0 },
                                ruta = try { child.child("ruta").value<String>() } catch (e: Exception) { "" },
                                fecha = try { child.child("fecha").value<String>() } catch (e: Exception) { "" },
                                horario = try { child.child("horario").value<String>() } catch (e: Exception) { "" },
                                asientos = try { child.child("asientos").value<String>() } catch (e: Exception) { "" },
                                timestamp = try { child.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (_: Exception) { try { child.child("timestamp").value<Long>() } catch (_: Exception) { 0L } },
                                estatus = try { child.child("estatus").value<String>() } catch (e: Exception) { "pendiente" }
                            ))
                        } catch (e: Exception) { /* skip invalid */ }
                    }
                    _solicitudesFactura.value = lista.sortedByDescending { it.timestamp }
                } else {
                    _solicitudesFactura.value = emptyList()
                }
            } catch (e: Exception) {
                _solicitudesFactura.value = emptyList()
            }
            _isLoading.value = false
        }
    }

    /** Envía la solicitud al contador vía SendGrid (server-side), marca enviada y audita. */
    fun enviarSolicitudAlContador(id: String, onResult: (Boolean) -> Unit = {}) {
        viewModelScope.launch {
            try {
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("solicitudId", kotlinx.serialization.json.JsonPrimitive(id))
                    })
                }
                val response = client.post(network.CloudFunctions.ENVIAR_SOLICITUD_FACTURA) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val txt = response.body<String>()
                if (txt.contains("\"success\":true") || txt.contains("\"success\": true")) {
                    _mensajeGlobal.value = "Factura procesada. Solicitud enviada al contador."
                    cargarSolicitudesFactura()
                    onResult(true)
                } else {
                    _mensajeGlobal.value = if (txt.contains("correo de contador")) "Configura primero el correo del contador." else "Error al enviar la solicitud al contador."
                    onResult(false)
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.enviarSolicitudAlContador")
                _mensajeGlobal.value = "Error al enviar la solicitud: ${e.message}"
                onResult(false)
            }
        }
    }

    fun marcarFacturaComoEnviada(id: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            try {
                val db = network.FirebaseClient.database
                db.reference("solicitudes_factura/$id/estatus").setValue("enviada")
                auditarCliente("factura_enviada", id, "Marcó solicitud como enviada (manual)")
                cargarSolicitudesFactura()
            } catch (e: Exception) { /* ignore */ }
        }
    }

    fun marcarFacturaComoFacturada(id: String) {
        viewModelScope.launch {
            if (bloqueadoPorVersion()) return@launch
            try {
                val db = network.FirebaseClient.database
                db.reference("solicitudes_factura/$id/estatus").setValue("facturada")
                auditarCliente("factura_facturada", id, "Marcó solicitud como facturada")
                cargarSolicitudesFactura()
            } catch (e: Exception) { /* ignore */ }
        }
    }

    // ============================================================================
    // Diagnóstico del sistema — solo desarrollador.
    // Llama a CF que verifica SendGrid/Stripe/RTDB/Auth/config/logs.
    // ============================================================================
    private val _diagnostico = MutableStateFlow<DiagnosticoResumen?>(null)
    val diagnostico: StateFlow<DiagnosticoResumen?> = _diagnostico.asStateFlow()

    private val _cargandoDiagnostico = MutableStateFlow(false)
    val cargandoDiagnostico: StateFlow<Boolean> = _cargandoDiagnostico.asStateFlow()

    private val _errorDiagnostico = MutableStateFlow<String?>(null)
    val errorDiagnostico: StateFlow<String?> = _errorDiagnostico.asStateFlow()

    fun ejecutarDiagnostico() {
        viewModelScope.launch {
            _cargandoDiagnostico.value = true
            _errorDiagnostico.value = null
            try {
                val client = network.sharedHttpClient
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                    })
                }
                val response = client.post(network.CloudFunctions.DIAGNOSTICO_SISTEMA) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val txt = response.body<String>()
                val json = try { kotlinx.serialization.json.Json.parseToJsonElement(txt).jsonObject } catch (e: Exception) { null }
                val result = json?.get("result")?.jsonObject
                val ok = result?.get("success")?.jsonPrimitive?.booleanOrNull ?: false
                if (!ok) {
                    _errorDiagnostico.value = result?.get("error")?.jsonPrimitive?.contentOrNull ?: "Diagnóstico falló"
                    _cargandoDiagnostico.value = false
                    return@launch
                }
                val resumenJson = result["resumen"]?.jsonObject ?: return@launch run {
                    _errorDiagnostico.value = "Respuesta sin resumen"; _cargandoDiagnostico.value = false
                }
                _diagnostico.value = DiagnosticoResumen(
                    timestamp = resumenJson["timestamp"]?.jsonPrimitive?.contentOrNull ?: "",
                    okTotal = resumenJson["okTotal"]?.jsonPrimitive?.intOrNull ?: 0,
                    failTotal = resumenJson["failTotal"]?.jsonPrimitive?.intOrNull ?: 0,
                    checks = resumenJson["checks"]?.jsonArray?.mapNotNull { c ->
                        val obj = c.jsonObject
                        DiagnosticoCheck(
                            nombre = obj["nombre"]?.jsonPrimitive?.contentOrNull ?: "",
                            ok = obj["ok"]?.jsonPrimitive?.booleanOrNull ?: false,
                            ms = obj["ms"]?.jsonPrimitive?.intOrNull ?: 0,
                            error = obj["error"]?.jsonPrimitive?.contentOrNull ?: "",
                            metaJson = obj["meta"]?.toString() ?: "{}"
                        )
                    } ?: emptyList()
                )
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AdminViewModel.ejecutarDiagnostico")
                _errorDiagnostico.value = "Error de conexión: ${e.message}"
            }
            _cargandoDiagnostico.value = false
        }
    }
}

data class DiagnosticoResumen(
    val timestamp: String,
    val okTotal: Int,
    val failTotal: Int,
    val checks: List<DiagnosticoCheck>
)

data class DiagnosticoCheck(
    val nombre: String,
    val ok: Boolean,
    val ms: Int,
    val error: String,
    val metaJson: String
)
